import { useWindowResize } from '@toggle/helpers';
import { TransformedXAxisType } from '@toggle/toggle';
import { getInstanceByDom, init } from 'echarts';
import * as Types from 'echarts/types/dist/echarts';
import React, { useEffect, useRef, useState } from 'react';

import {
  ApacheChartBase,
  createApacheConfig,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_CHART_WIDTH,
  SingleSeries,
  Y_AXIS_LABEL_PERCENTAGE,
} from '../apache-config';
import { RemoteLegend } from '../RemoteLegend';
import { tooltipFormatter } from '../utils';
import { formatAxis } from './utils';

export interface BasicBarChartProps extends ApacheChartBase {
  chartType: 'apache-bar';
  axis: TransformedXAxisType;
  series: SingleSeries[];
  isHorizontal?: boolean;
}

export const BasicBarChart = ({
  theme = 'dark_default',
  chartType,
  axis,
  series,
  isHorizontal = false,
  onInit,
  isModal,
}: BasicBarChartProps) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);

  const [legendInstance, setLegendInstance] = useState<Types.ECharts | null>(
    null
  );
  const { width, height } = useWindowResize({ targetRef: chartRef });

  useEffect(() => {
    if (chartRef.current) {
      const chartDom = getInstanceByDom(chartRef.current);
      chartDom?.resize({ width, height });
    }
  }, [width, height]);

  useEffect(() => {
    const chartDom = init(chartRef.current, theme, {
      width: width || DEFAULT_CHART_WIDTH,
      height: height || DEFAULT_CHART_HEIGHT,
    });

    setLegendInstance(chartDom);

    const seriesData: echarts.SeriesOption[] = series.map(i => ({
      name: i.name,
      data: i.data,
      type: 'bar',
    }));

    const chartWidth = width || DEFAULT_CHART_WIDTH;
    const yAxisLabelWidth = chartWidth * Y_AXIS_LABEL_PERCENTAGE;

    const option = createApacheConfig({
      theme,
      isModal,
      yAxisLabelWidth,
      options: {
        ...formatAxis(axis, isHorizontal),
        tooltip: {
          formatter: params => {
            return tooltipFormatter({
              isShowTitle: false,
              isShowMarker: true,
              params: params as Types.DefaultLabelFormatterCallbackParams,
            });
          },
        },
        grid: {
          bottom: '5%',
        },
        legend: { show: false },
        series: seriesData,
      },
    });

    chartDom.setOption(option);

    if (wrapperRef.current) {
      onInit?.(wrapperRef.current);
    }

    return () => {
      chartDom.dispose();
      setLegendInstance(null);
    };
  }, [chartType, theme, axis, series, width, height, isModal]);

  return (
    <div ref={wrapperRef}>
      <div ref={chartRef} />
      {legendInstance && (
        <RemoteLegend
          theme={theme}
          legendData={series.map(s => ({ name: s.name, type: 'bar' }))}
          targetChart={legendInstance}
        />
      )}
    </div>
  );
};
